import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, LogOut, ShieldCheck, MessageSquare, LayoutDashboard } from "lucide-react";
import ZtkLogo from "@/components/ZtkLogo";
import { toast } from "sonner";

const LINKS = [
  { name: "Start", href: "/", id: "start" },
  { name: "Organy", href: "/organy", id: "organy" },
  { name: "Status prawny", href: "/status", id: "status" },
  { name: "Konferencje i szkolenia", href: "/szkolenia", id: "szkolenia" },
  { name: "Kontakt", href: "/kontakt", id: "kontakt" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success("Wylogowano pomyślnie");
    navigate("/");
  };

  const isActive = (href) => (href === "/" ? location.pathname === "/" : location.pathname.startsWith(href));

  const NavItems = ({ onClick }) => (
    <>
      {LINKS.map((l) => (
        <Link
          key={l.id}
          to={l.href}
          onClick={onClick}
          data-testid={`nav-link-${l.id}`}
          className={`whitespace-nowrap text-sm font-medium transition-colors hover:text-ztk-blue ${
            isActive(l.href) ? "text-ztk-blue" : "text-ztk-navy/80 dark:text-slate-300"
          }`}
        >
          {l.name}
        </Link>
      ))}
      <Link
        to="/forum"
        onClick={onClick}
        data-testid="nav-link-forum"
        className={`flex items-center gap-1.5 whitespace-nowrap text-sm font-medium transition-colors hover:text-ztk-blue ${
          isActive("/forum") ? "text-ztk-blue" : "text-ztk-navy/80 dark:text-slate-300"
        }`}
      >
        <MessageSquare className="h-4 w-4" /> Forum
        <span className="rounded-full bg-ztk-yellow px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-ztk-navydark">
          Członkowie
        </span>
      </Link>
    </>
  );

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all ${
        scrolled
          ? "glass border-slate-200/80 shadow-sm dark:border-slate-800/80"
          : "border-transparent bg-background/60"
      }`}
    >
      <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <ZtkLogo />

        <nav className="hidden items-center gap-6 xl:flex">
          <NavItems />
        </nav>

        <div className="hidden items-center gap-2 xl:flex">
          {user && user.role === "admin" && (
            <Button asChild variant="ghost" size="sm" data-testid="nav-admin-btn">
              <Link to="/_ztkig_admin" className="gap-1.5 text-ztk-navy dark:text-slate-200">
                <LayoutDashboard className="h-4 w-4" /> Panel CRM
              </Link>
            </Button>
          )}
          {user ? (
            <>
              <span className="flex items-center gap-1.5 text-sm font-medium text-ztk-navy dark:text-slate-200" data-testid="nav-user-name">
                {user.role === "admin" && <ShieldCheck className="h-4 w-4 text-ztk-blue" />}
                {user.name}
              </span>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="nav-logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="nav-login-btn">
                <Link to="/login">Zaloguj się</Link>
              </Button>
              <Button asChild size="sm" className="bg-ztk-blue hover:bg-ztk-navy800" data-testid="nav-register-btn">
                <Link to="/rejestracja">Dołącz do Izby</Link>
              </Button>
            </>
          )}
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="xl:hidden">
            <Button variant="ghost" size="icon" data-testid="nav-mobile-toggle">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[280px]">
            <div className="mt-8 flex flex-col gap-5">
              <NavItems onClick={() => setOpen(false)} />
              <div className="mt-4 flex flex-col gap-2 border-t pt-4">
                {user && user.role === "admin" && (
                  <Button asChild variant="secondary" onClick={() => setOpen(false)} data-testid="nav-admin-btn-mobile">
                    <Link to="/_ztkig_admin">Panel CRM</Link>
                  </Button>
                )}
                {user ? (
                  <Button variant="outline" onClick={() => { setOpen(false); handleLogout(); }}>
                    Wyloguj ({user.name})
                  </Button>
                ) : (
                  <>
                    <Button asChild variant="outline" onClick={() => setOpen(false)}>
                      <Link to="/login">Zaloguj się</Link>
                    </Button>
                    <Button asChild className="bg-ztk-blue" onClick={() => setOpen(false)}>
                      <Link to="/rejestracja">Dołącz do Izby</Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <div className="cable-accent h-[3px] w-full" />
    </header>
  );
}
